import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import V4Tabs from './V4Tabs';

const DocumentsListV4 = () => {
  const { getAuthHeaders } = useAuth();
  const [docs, setDocs] = useState({ items: [], total: 0 });
  const [q, setQ] = useState('');
  const [linkedFilter, setLinkedFilter] = useState('');
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerMarkdown, setViewerMarkdown] = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);
  const [editorMode, setEditorMode] = useState(false);
  const [editorText, setEditorText] = useState('');
  const [editingDocId, setEditingDocId] = useState(null);
  const [viewerBaseDir, setViewerBaseDir] = useState('');
  const [widthPct, setWidthPct] = useState(50); // default 50%
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const [jobViewerOpen, setJobViewerOpen] = useState(false);
  const [jobDoc, setJobDoc] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const jobPollRef = useRef(null);
  const jobDragRef = useRef(null);
  const [jobWidthPct, setJobWidthPct] = useState(50);
  const [selectedIds, setSelectedIds] = useState([]);
  const [revectorizeScope, setRevectorizeScope] = useState('embeddings_only');
  const [batchNotice, setBatchNotice] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDoc, setDetailDoc] = useState(null);
  const [detailProducts, setDetailProducts] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailWidthPct, setDetailWidthPct] = useState(50);
  const [detailDragging, setDetailDragging] = useState(false);
  const [productCodesInput, setProductCodesInput] = useState('');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, doc: null });

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset), q });
      if (linkedFilter) params.set('linked_products', linkedFilter);
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/list?${params.toString()}`, {
        headers: { ...getAuthHeaders() },
      });
      const data = await res.json();
      setDocs({ items: Array.isArray(data.items) ? data.items : [], total: data.total || 0 });
      setSelectedIds([]);
    } catch (_) {
      setDocs({ items: [], total: 0 });
    }
    setLoading(false);
  }, [getAuthHeaders, limit, offset, q, linkedFilter]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Poll only rows that are in-progress to refresh status/progress
  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const items = Array.isArray(docs.items) ? docs.items : [];
        const active = items.filter((d) => ['queued','processing'].includes(String(d.status||'').toLowerCase()) && d.job_id);
        if (active.length === 0) return;
        const headers = { ...getAuthHeaders() };
        const updates = await Promise.all(active.map(async (d) => {
          try {
            const r = await fetch(`${API_BASE_URL}/api/v4/documents/jobs/${d.job_id}`, { headers });
            if (!r.ok) return null;
            const j = await r.json();
            return { id: d.id, job_id: d.job_id, status: j.status, progress: j.progress, metadata: j.metadata };
          } catch (_) { return null; }
        }));
        const map = new Map();
        for (const u of updates) if (u && u.id) map.set(u.id, u);
        if (map.size > 0) {
          setDocs((prev) => ({
            ...prev,
            items: (prev.items || []).map((d) => {
              const u = map.get(d.id);
              return u ? { ...d, status: u.status || d.status, progress: u.progress ?? d.progress } : d;
            })
          }));
        }
      } catch (_) { /* ignore */ }
    }, 5000);
    return () => clearInterval(timer);
  }, [docs.items, getAuthHeaders]);

  const viewMarkdown = async (doc, openEditor = false) => {
    const url = doc.markdown_url ? `${API_BASE_URL}${doc.markdown_url}` : null;
    if (!url) return;
    try {
      setViewerTitle(doc.original_filename || 'Document');
      setViewerMarkdown('');
      setViewerLoading(true);
      setViewerOpen(true);
      setEditorMode(openEditor);
      setEditingDocId(doc.id);
      const abs = new URL(url, window.location.origin);
      const baseDir = abs.pathname.substring(0, abs.pathname.lastIndexOf('/'));
      setViewerBaseDir(`${API_BASE_URL}${baseDir}`);
      const res = await fetch(url, { headers: { ...getAuthHeaders() } });
      const text = await res.text();
      setViewerMarkdown(text);
      setEditorText(text);
    } catch (e) {
      setViewerMarkdown(`Failed to load markdown. ${String(e)}`);
    }
    setViewerLoading(false);
  };

  const toggleEditor = () => {
    setEditorMode((v) => !v);
  };

  const saveMarkdown = async () => {
    if (!editingDocId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/${editingDocId}/markdown`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ markdown: editorText })
      });
      if (res.ok) {
        setViewerMarkdown(editorText);
        alert('Saved');
      } else {
        const t = await res.text();
        alert(`Save failed: ${t}`);
      }
    } catch (e) {
      alert(`Save error: ${String(e)}`);
    }
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging) return;
      const vw = window.innerWidth || 1;
      const x = e.clientX;
      const newPct = Math.max(50, Math.min(75, ((vw - x) / vw) * 100));
      setWidthPct(newPct);
    };
    const onUp = () => setDragging(false);
    if (dragging) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  useEffect(() => {
    const onMove = (e) => {
      if (!detailDragging) return;
      const vw = window.innerWidth || 1;
      const x = e.clientX;
      const newPct = Math.max(50, Math.min(75, ((vw - x) / vw) * 100));
      setDetailWidthPct(newPct);
    };
    const onUp = () => setDetailDragging(false);
    if (detailDragging) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [detailDragging]);

  // Dragging for job viewer
  useEffect(() => {
    const onMove = (e) => {
      if (!jobViewerOpen || !jobDragRef.current) return;
      const vw = window.innerWidth || 1;
      const x = e.clientX;
      const newPct = Math.max(50, Math.min(75, ((vw - x) / vw) * 100));
      setJobWidthPct(newPct);
    };
    const onUp = () => { /* no local state */ };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [jobViewerOpen]);

  const openJobViewer = async (doc) => {
    try { if (jobPollRef.current) clearInterval(jobPollRef.current); } catch {}
    setJobDoc(doc);
    setJobDetail(null);
    setJobViewerOpen(true);
    const headers = { ...getAuthHeaders() };
    const fetchOnce = async () => {
      if (!doc?.job_id) return;
      try {
        const r = await fetch(`${API_BASE_URL}/api/v4/documents/jobs/${doc.job_id}`, { headers });
        if (r.ok) setJobDetail(await r.json());
      } catch (_) { /* ignore */ }
    };
    await fetchOnce();
    jobPollRef.current = setInterval(fetchOnce, 5000);
  };

  const closeJobViewer = () => {
    try { if (jobPollRef.current) clearInterval(jobPollRef.current); } catch {}
    setJobViewerOpen(false);
    setJobDoc(null);
    setJobDetail(null);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    const items = docs.items || [];
    if (!items.length) return;
    const ids = items.map((d) => d.id);
    const allSelected = ids.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : ids);
  };

  const runRevectorize = async () => {
    if (!selectedIds.length) {
      setBatchNotice('Select at least one document to re-vectorize.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/batches/revectorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ doc_ids: selectedIds, scope: revectorizeScope }),
      });
      const data = await res.json();
      if (!res.ok) {
        setBatchNotice(`Re-vectorize failed: ${data?.detail || 'error'}`);
        return;
      }
      setBatchNotice(`Re-vectorize batch queued: ${data.batch_id}`);
      setSelectedIds([]);
    } catch (e) {
      setBatchNotice(`Re-vectorize error: ${String(e)}`);
    }
  };

  const openDetailDrawer = async (doc) => {
    setDetailDoc(doc);
    setDetailProducts([]);
    setProductCodesInput('');
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/${doc.id}/products`, { headers: { ...getAuthHeaders() } });
      const data = await res.json();
      const products = Array.isArray(data.products) ? data.products : [];
      setDetailProducts(products);
      setProductCodesInput(products.map((p) => p.product_code).filter(Boolean).join(', '));
    } catch (_) {
      setDetailProducts([]);
    }
    setDetailLoading(false);
  };

  const saveLinkedProducts = async () => {
    if (!detailDoc) return;
    const codes = productCodesInput.split(',').map((c) => c.trim()).filter(Boolean);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/${detailDoc.id}/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ product_codes: codes }),
      });
      if (!res.ok) {
        setBatchNotice('Failed to update product links.');
        return;
      }
      setDetailProducts(codes.map((c) => ({ product_code: c })));
      setBatchNotice('Product links updated.');
      loadDocs();
    } catch (_) {
      setBatchNotice('Failed to update product links.');
    }
  };

  const deleteDocument = async (doc) => {
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/${doc.id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (res.ok) loadDocs();
  };

  return (
    <div className="container-fluid py-4 px-3 px-lg-4">
      <h3 className="mb-3">Documents v4</h3>
      <V4Tabs />
      <div className="card">
        <div className="card-body">
          {batchNotice && (
            <div className="alert alert-info py-2" role="alert">
              {batchNotice}
            </div>
          )}
          <div className="row g-2 align-items-end mb-3">
            <div className="col-12 col-lg-5">
              <label className="form-label">Search</label>
              <div className="input-group">
                <input className="form-control" placeholder="Search by filename, job or batch" value={q} onChange={(e) => setQ(e.target.value)} />
                <button className="btn btn-outline-primary" onClick={() => { setOffset(0); loadDocs(); }}>Search</button>
              </div>
            </div>
            <div className="col-12 col-md-4 col-lg-3">
              <label className="form-label">Product links</label>
              <select className="form-select" value={linkedFilter} onChange={(e) => { setLinkedFilter(e.target.value); setOffset(0); }}>
                <option value="">All documents</option>
                <option value="with">With products</option>
                <option value="without">Without products</option>
              </select>
            </div>
            <div className="col-12 col-md-3 col-lg-2">
              <label className="form-label">Page size</label>
              <select className="form-select" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="col-12 col-md-5 col-lg-2">
              <div className="text-muted small">Total: {docs.total}</div>
            </div>
          </div>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-12 col-md-4 col-lg-3">
              <label className="form-label">Re-vectorize scope</label>
              <select className="form-select" value={revectorizeScope} onChange={(e) => setRevectorizeScope(e.target.value)}>
                <option value="embeddings_only">Embeddings + Pinecone</option>
                <option value="full">Full pipeline (convert + embed + upsert)</option>
              </select>
            </div>
            <div className="col-12 col-md-4 col-lg-3">
              <button className="btn btn-primary w-100" onClick={runRevectorize} disabled={!selectedIds.length}>
                Re-vectorize Selected ({selectedIds.length})
              </button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input type="checkbox" className="form-check-input" checked={docs.items?.length > 0 && docs.items.every((d) => selectedIds.includes(d.id))} onChange={toggleSelectAll} />
                  </th>
                  <th>File</th>
                  <th>Vector ID</th>
                  <th>Status</th>
                  <th>Size</th>
                  <th>Progress</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(docs.items || []).map((d) => (
                  <tr key={d.id}>
                    <td>
                      <input type="checkbox" className="form-check-input" checked={selectedIds.includes(d.id)} onChange={() => toggleSelect(d.id)} />
                    </td>
                    <td>{d.original_filename}</td>
                    <td style={{ wordBreak: 'break-all' }}>{d.pinecone_vector_id || '-'}</td>
                    <td>
                      <span
                        role="button"
                        className={`badge ${String(d.status||'').toLowerCase()==='completed'?'bg-success':String(d.status||'').toLowerCase()==='failed'?'bg-danger':String(d.status||'').toLowerCase()==='cancelled'?'bg-secondary':'bg-warning text-dark'}`}
                        title="View processing details"
                        onClick={() => openJobViewer(d)}
                      >
                        {d.status}
                      </span>
                    </td>
                    <td>{typeof d.file_size === 'number' ? `${(d.file_size / 1024 / 1024).toFixed(2)} MB` : '-'}</td>
                    <td>{d.progress}%</td>
                    <td>{new Date(d.created_at).toLocaleString()}</td>
                    <td className="text-nowrap" style={{ minWidth: 180 }}>
                      <div className="d-flex flex-wrap gap-2">
                        <button className="btn btn-outline-primary btn-sm" disabled={!d.markdown_url} onClick={() => viewMarkdown(d)}>Preview</button>
                        <button className="btn btn-outline-secondary btn-sm" disabled={!d.markdown_url} onClick={() => viewMarkdown(d, true)}>Edit</button>
                        <button className="btn btn-outline-dark btn-sm" onClick={() => openDetailDrawer(d)}>Details</button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => setConfirmDelete({ open: true, doc: d })}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!docs.items || docs.items.length === 0) && (
                  <tr><td colSpan="8" className="text-center text-muted py-3">{loading ? 'Loading…' : 'No documents found'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button className="btn btn-outline-secondary btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Prev</button>
            <button className="btn btn-outline-secondary btn-sm" disabled={(offset + (docs.items?.length || 0)) >= docs.total} onClick={() => setOffset(offset + limit)}>Next</button>
          </div>
        </div>
      </div>
      {detailOpen && (
        <>
          <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1049 }} onClick={() => setDetailOpen(false)} />
          <div className="position-fixed top-0 end-0 h-100 bg-white shadow" style={{ width: `${detailWidthPct}vw`, maxWidth: '80vw', minWidth: '50vw', zIndex: 1050, transition: 'transform 0.25s ease', transform: 'translateX(0)' }}>
            <div className="d-flex align-items-center justify-content-between border-bottom px-3 py-2">
              <div className="fw-semibold text-truncate" title={detailDoc?.original_filename || ''}>
                {detailDoc?.original_filename || 'Document details'}
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setDetailOpen(false)}>Close</button>
            </div>
            <div className="position-absolute top-0 start-0 h-100" style={{ width: 6, cursor: 'ew-resize', zIndex: 1051 }} onMouseDown={() => setDetailDragging(true)} />
            <div className="p-3 overflow-auto h-100">
              {detailLoading && <div className="text-muted">Loading…</div>}
              {!detailLoading && detailDoc && (
                <>
                  <div className="row g-2 mb-3">
                    <div className="col-12 col-md-6"><div className="small text-muted">Document ID</div><div className="text-break">{detailDoc.id}</div></div>
                    <div className="col-12 col-md-6"><div className="small text-muted">Job ID</div><div className="text-break">{detailDoc.job_id || '-'}</div></div>
                    <div className="col-12 col-md-6"><div className="small text-muted">Batch ID</div><div className="text-break">{detailDoc.batch_id || '-'}</div></div>
                    <div className="col-12 col-md-6"><div className="small text-muted">Vector ID</div><div className="text-break">{detailDoc.pinecone_vector_id || '-'}</div></div>
                    <div className="col-12 col-md-4"><div className="small text-muted">Status</div><div>{detailDoc.status}</div></div>
                    <div className="col-12 col-md-4"><div className="small text-muted">Progress</div><div>{detailDoc.progress}%</div></div>
                    <div className="col-12 col-md-4"><div className="small text-muted">Size</div><div>{typeof detailDoc.file_size === 'number' ? `${(detailDoc.file_size / 1024 / 1024).toFixed(2)} MB` : '-'}</div></div>
                    <div className="col-12"><div className="small text-muted">Stored Path</div><div className="text-break">{detailDoc.stored_path || '-'}</div></div>
                    <div className="col-12"><div className="small text-muted">Markdown Path</div><div className="text-break">{detailDoc.markdown_path || '-'}</div></div>
                  </div>

                  <div className="mb-3">
                    <div className="fw-semibold mb-2">Linked Products</div>
                    {detailProducts.length ? (
                      <ul className="list-group mb-2">
                        {detailProducts.map((p, i) => (
                          <li key={`${p.product_code}-${i}`} className="list-group-item d-flex justify-content-between align-items-center">
                            <span className="text-break">{p.product_code}</span>
                            {p.product_name && <span className="small text-muted">{p.product_name}</span>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-muted small mb-2">No linked products.</div>
                    )}
                    <label className="form-label">Link products (comma-separated codes)</label>
                    <textarea className="form-control" rows={2} value={productCodesInput} onChange={(e) => setProductCodesInput(e.target.value)} />
                    <button className="btn btn-sm btn-primary mt-2" onClick={saveLinkedProducts}>Save Links</button>
                  </div>

                  <div>
                    <div className="fw-semibold mb-2">Metadata</div>
                    <pre className="bg-light p-2 rounded small">{JSON.stringify(detailDoc.metadata || {}, null, 2)}</pre>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
      {viewerOpen && (
        <>
          <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1049 }} onClick={() => setViewerOpen(false)} />
          <div className="position-fixed top-0 end-0 h-100 bg-white shadow" style={{ width: `${widthPct}vw`, maxWidth: '80vw', minWidth: '50vw', zIndex: 1050, transition: 'transform 0.25s ease', transform: 'translateX(0)' }}>
            <div className="d-flex align-items-center justify-content-between border-bottom px-3 py-2">
              <div className="fw-semibold text-truncate" title={viewerTitle}>{viewerTitle}</div>
              <div className="d-flex align-items-center gap-2">
                <div className="form-check form-switch me-2">
                  <input className="form-check-input" type="checkbox" id="toggleEditor"
                    checked={editorMode} onChange={toggleEditor} />
                  <label className="form-check-label small" htmlFor="toggleEditor">Editor</label>
                </div>
                {editorMode && (
                  <button className="btn btn-sm btn-primary" onClick={saveMarkdown}>Save</button>
                )}
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setViewerOpen(false)}>Close</button>
              </div>
            </div>
            <div className="position-absolute top-0 start-0 h-100" style={{ width: 6, cursor: 'ew-resize', zIndex: 1051 }} onMouseDown={() => setDragging(true)} ref={dragRef} />
            <div className="p-3 overflow-auto h-100" style={{ paddingTop: 12 }}>
              {viewerLoading ? (
                <div className="text-muted">Loading…</div>
              ) : editorMode ? (
                <textarea className="form-control" style={{height:'100%', minHeight: '60vh'}} value={editorText} onChange={(e) => setEditorText(e.target.value)} />
              ) : (
                <div className="markdown-body">
                  <ReactMarkdown
                    components={{
                      img: ({node, ...props}) => {
                        try {
                          const src = props.src || '';
                          const isAbsolute = /^https?:\/\//i.test(src);
                          const resolved = isAbsolute ? src : `${viewerBaseDir}/${src.replace(/^\.\//,'')}`;
                          return <img alt={props.alt || ''} src={resolved} style={{maxWidth:'100%'}} />
                        } catch (_) {
                          return <img alt={props.alt || ''} {...props} style={{maxWidth:'100%'}} />
                        }
                      }
                    }}
                  >{viewerMarkdown}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {jobViewerOpen && (
        <>
          <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1049 }} onClick={closeJobViewer} />
          <div className="position-fixed top-0 end-0 h-100 bg-white shadow" style={{ width: `${jobWidthPct}vw`, maxWidth: '75vw', minWidth: '50vw', zIndex: 1050, transition: 'transform 0.25s ease', transform: 'translateX(0)' }}>
            <div className="d-flex align-items-center justify-content-between border-bottom px-3 py-2">
              <div className="fw-semibold text-truncate" title={jobDoc?.original_filename || ''}>
                {jobDoc?.original_filename || 'Job'}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={closeJobViewer}>Close</button>
              </div>
            </div>
            <div className="position-absolute top-0 start-0 h-100" style={{ width: 6, cursor: 'ew-resize', zIndex: 1051 }} onMouseDown={() => (jobDragRef.current = true)} onMouseUp={() => (jobDragRef.current = false)} />
            <div className="p-3 overflow-auto h-100" style={{ paddingTop: 12 }}>
              {!jobDetail ? (
                <div className="text-muted">Loading…</div>
              ) : (
                <div>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <span className={`badge ${String(jobDetail.status||'').toLowerCase()==='completed'?'bg-success':String(jobDetail.status||'').toLowerCase()==='failed'?'bg-danger':String(jobDetail.status||'').toLowerCase()==='cancelled'?'bg-secondary':'bg-warning text-dark'}`}>{jobDetail.status}</span>
                    <div className="progress" style={{ width: 160 }}>
                      <div className="progress-bar" style={{ width: `${Number(jobDetail.progress||0)}%` }}>{Number(jobDetail.progress||0)}%</div>
                    </div>
                  </div>
                  {Array.isArray(jobDetail?.metadata?.pipeline) ? (
                    <ul className="list-group">
                      {jobDetail.metadata.pipeline.map((s, i) => (
                        <li key={i} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-semibold">{s.name}</div>
                              <div className="small text-muted">{s.message}</div>
                              <div className="small text-muted">
                                {s.started_at && <span className="me-3">Start: {s.started_at}</span>}
                                {s.finished_at && <span className="me-3">End: {s.finished_at}</span>}
                                {typeof s.duration_seconds === 'number' && <span>Duration: {s.duration_seconds.toFixed(1)}s</span>}
                              </div>
                            </div>
                            <span className={`badge ${s.status==='completed'?'bg-success':s.status==='failed'?'bg-danger':'bg-warning text-dark'}`}>{s.status}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="small text-muted">No step details yet.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {confirmDelete.open && (
        <>
          <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1060 }} onClick={() => setConfirmDelete({ open: false, doc: null })} />
          <div className="position-fixed top-50 start-50 translate-middle bg-white shadow rounded" style={{ zIndex: 1061, minWidth: 320, maxWidth: 520 }}>
            <div className="border-bottom px-3 py-2 fw-semibold">Confirm delete</div>
            <div className="p-3">
              <div className="mb-3">Delete {confirmDelete.doc?.original_filename || 'this document'}?</div>
              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setConfirmDelete({ open: false, doc: null })}>Cancel</button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={async () => {
                    const target = confirmDelete.doc;
                    setConfirmDelete({ open: false, doc: null });
                    if (target) await deleteDocument(target);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentsListV4;


